<template>
  <div class="recharge-page">
    <!-- 当前到期状态 -->
    <el-card class="status-card" shadow="never">
      <div class="status-row">
        <div class="status-icon"><i class="el-icon-time"></i></div>
        <div class="status-info">
          <div class="status-label">系统账号有效期</div>
          <div class="status-date" :class="{ expired: isExpired }">
            {{ expireText }}
          </div>
          <div v-if="expireDays !== null" class="status-sub" :class="{ expired: isExpired }">
            {{ isExpired ? '已到期，续费后立即恢复使用' : '续费从当前到期日顺延，剩余时长不受影响' }}
          </div>
        </div>
      </div>
    </el-card>

    <!-- 续费套餐 -->
    <el-card class="tool-card" shadow="never">
      <div class="tool-row">
        <div class="tool-title">选择续费套餐</div>
        <div class="tool-sub">扫码支付，支付完成后自动顺延有效期</div>
      </div>
      <div v-loading="loading" class="package-grid">
        <div
          v-for="p in packages"
          :key="p.id"
          class="package-card"
          :class="{ selected: curPackageId === p.id }"
          @click="curPackageId = p.id"
        >
          <div class="pkg-name">{{ p.name }}</div>
          <div class="pkg-months">{{ p.months }}<span class="pkg-unit">个月</span></div>
          <div class="pkg-price"><span class="pkg-symbol">¥</span>{{ fmtMoney(p.price) }}</div>
          <el-button
            type="primary"
            size="small"
            class="pkg-btn"
            :loading="ordering && curPackageId === p.id"
            @click.stop="doOrder(p)"
          >立即续费</el-button>
        </div>
        <div v-if="!loading && packages.length === 0" class="pkg-empty">暂无可用套餐</div>
      </div>
    </el-card>

    <!-- 我的续费订单 -->
    <el-card class="tool-card" shadow="never">
      <div class="tool-row">
        <div class="tool-title">续费订单</div>
        <el-radio-group v-model="statusFilter" size="small" @change="loadOrders">
          <el-radio-button label="">全部</el-radio-button>
          <el-radio-button label="0">待支付</el-radio-button>
          <el-radio-button label="1">已支付</el-radio-button>
          <el-radio-button label="2">已关闭</el-radio-button>
        </el-radio-group>
      </div>
      <el-table
        v-loading="ordersLoading"
        :data="orders"
        :header-cell-style="{ background: '#F7F9FC', color: '#18233C', fontWeight: 600 }"
        empty-text="暂无续费订单"
      >
        <el-table-column prop="order_sn" label="订单号" min-width="200" />
        <el-table-column prop="package_name" label="套餐" min-width="120" />
        <el-table-column label="时长" width="100" align="center">
          <template slot-scope="{ row }">{{ row.months }} 个月</template>
        </el-table-column>
        <el-table-column label="金额" width="120" align="right">
          <template slot-scope="{ row }">
            <span class="price-main">¥ {{ fmtMoney(row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="payStatusType(row.status)" size="small">{{ row.status_text }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="支付时间" min-width="160">
          <template slot-scope="{ row }">
            <span class="time-text">{{ fmtDateTime(row.paytime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" align="center">
          <template slot-scope="{ row }">
            <el-button v-if="parseInt(row.status) === 0" type="text" size="small" @click="openPay(row)">去支付</el-button>
            <span v-else>—</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 扫码支付弹窗 -->
    <el-dialog title="扫码支付" :visible.sync="payVisible" width="440px" :close-on-click-modal="false" @closed="stopPolling">
      <div class="pay-summary">
        <div class="pay-row"><span>订单号</span><span class="pay-sn">{{ payOrder.order_sn }}</span></div>
        <div class="pay-row"><span>套餐时长</span><span>{{ payOrder.months }} 个月</span></div>
        <div class="pay-row"><span>应付金额</span><span class="pay-amount">¥ {{ fmtMoney(payOrder.amount) }}</span></div>
      </div>

      <!-- 支付方式切换 -->
      <div class="pay-type-tabs">
        <div
          class="pay-type"
          :class="{ active: paytype === 'wechat' }"
          @click="switchPaytype('wechat')"
        >
          <i class="pay-icon p-wechat">微</i>微信支付
        </div>
        <div
          class="pay-type"
          :class="{ active: paytype === 'alipay' }"
          @click="switchPaytype('alipay')"
        >
          <i class="pay-icon p-alipay">支</i>支付宝
        </div>
      </div>

      <div v-loading="payLoading" class="qr-wrap">
        <template v-if="qrUrl">
          <img :src="resolveAsset(qrUrl)" class="qr-img" alt="支付二维码" />
          <div class="qr-tip">请使用{{ paytype === 'wechat' ? '微信' : '支付宝' }}扫码支付</div>
        </template>
        <div v-else class="qr-empty" :class="{ 'qr-error': qrError }">
          <template v-if="qrError">
            <i class="el-icon-warning-outline"></i>
            <div>支付二维码生成失败</div>
            <div class="qr-error-sub">可能未开通该支付产品，请检查后重试</div>
            <el-button size="small" type="primary" class="qr-retry" :loading="payLoading" @click="switchPaytype(paytype)">重新生成</el-button>
          </template>
          <template v-else>
            <i class="el-icon-loading"></i> 正在生成支付二维码...
          </template>
        </div>
      </div>

      <div class="pay-hint">支付完成后本弹窗将自动关闭并更新有效期</div>
      <div slot="footer" class="dialog-footer">
        <el-button v-if="payLoading" disabled size="small">请扫码</el-button>
        <el-button size="small" @click="payVisible = false">取 消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { rechargePackageList, rechargeOrder, rechargePay, rechargeOrders } from '@/api/recharge'
import { API_BASE } from '@/config'

const ASSET_BASE = API_BASE.replace(/\/api\/?$/, '')

export default {
  name: 'RechargeIndex',
  data() {
    return {
      loading: false,
      packages: [],
      expireDate: null,
      curPackageId: 0,
      ordering: false,
      // 订单列表
      statusFilter: '',
      ordersLoading: false,
      orders: [],
      // 支付弹窗
      payVisible: false,
      payLoading: false,
      paytype: 'wechat',
      qrUrl: '',
      qrError: false,
      payOrder: {},
      pollTimer: null
    }
  },
  computed: {
    expireText() {
      if (!this.expireDate) return '长期有效'
      return '至 ' + this.expireDate
    },
    isExpired() {
      if (!this.expireDate) return false
      return this.expireDate < this.today()
    },
    expireDays() {
      if (!this.expireDate) return null
      if (this.isExpired) return -1
      return Math.max(0, Math.ceil((new Date(this.expireDate).getTime() - new Date().getTime()) / 86400000))
    }
  },
  created() {
    this.load()
    this.loadOrders()
  },
  methods: {
    today() {
      const d = new Date()
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    load() {
      this.loading = true
      rechargePackageList()
        .then(data => {
          this.packages = data.list || []
          this.expireDate = data.expire_date || null
        })
        .catch(() => { this.packages = [] })
        .finally(() => { this.loading = false })
    },
    loadOrders() {
      this.ordersLoading = true
      rechargeOrders({ status: this.statusFilter })
        .then(data => { this.orders = data.list || [] })
        .catch(() => { this.orders = [] })
        .finally(() => { this.ordersLoading = false })
    },
    fmtMoney(n) {
      return parseFloat(n || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fmtDateTime(ts) {
      if (!ts) return '—'
      const d = new Date(parseInt(ts) * 1000)
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    payStatusType(s) {
      s = parseInt(s)
      if (s === 1) return 'success'
      if (s === 2) return 'info'
      return 'warning'
    },
    resolveAsset(url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url)) return url
      if (/^data:/.test(url)) return url
      if (/^\//.test(url)) return ASSET_BASE + url
      return url
    },
    doOrder(p) {
      this.ordering = true
      this.curPackageId = p.id
      rechargeOrder(p.id)
        .then(data => {
          this.curPackageId = 0
          this.openPay({
            order_id: data.order_id,
            order_sn: data.order_sn,
            amount: data.amount,
            months: data.months
          })
          this.loadOrders()
        })
        .catch(() => { this.curPackageId = 0 })
        .finally(() => { this.ordering = false })
    },
    openPay(row) {
      this.payOrder = {
        order_id: row.order_id || row.id,
        order_sn: row.order_sn || '',
        amount: row.amount,
        months: row.months
      }
      this.payVisible = true
      this.switchPaytype('wechat')
    },
    switchPaytype(type) {
      if (!this.payOrder.order_id) return
      this.paytype = type
      this.qrUrl = ''
      this.qrError = false
      this.payLoading = true
      rechargePay(this.payOrder.order_id, type)
        .then(data => {
          this.qrUrl = data.qr_url || ''
          this.startPolling()
        })
        .catch(() => { this.qrError = true })
        .finally(() => { this.payLoading = false })
    },
    startPolling() {
      this.stopPolling()
      // 每 2.5 秒查询待支付订单是否已支付
      this.pollTimer = setInterval(() => {
        rechargeOrders({ status: 1 })
          .then(data => {
            const hit = (data.list || []).find(x => parseInt(x.id) === parseInt(this.payOrder.order_id))
            if (hit) {
              this.stopPolling()
              this.$message.success('续费成功，有效期已顺延')
              this.payVisible = false
              this.load()
              this.loadOrders()
            }
          })
          .catch(() => {})
      }, 2500)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    }
  }
}
</script>

<style scoped>
.recharge-page { padding: 0; }

.status-card {
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid #E5E9F2;
}
.status-row { display: flex; align-items: center; gap: 14px; }
.status-icon {
  width: 42px; height: 42px; border-radius: 10px;
  background: linear-gradient(135deg, #3668FC, #5B8DFF);
  color: #fff; font-size: 20px;
  display: flex; align-items: center; justify-content: center;
}
.status-label { font-size: 12px; color: #8A94AD; }
.status-date { font-size: 18px; font-weight: 700; color: #18233C; margin-top: 2px; }
.status-date.expired { color: #E5484D; }
.status-sub { font-size: 12px; color: #8A94AD; margin-top: 2px; }
.status-sub.expired { color: #E5484D; }

.tool-card { margin-bottom: 14px; border-radius: 10px; border: 1px solid #E5E9F2; }
.tool-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
.tool-title { font-size: 15px; font-weight: 700; color: #18233C; }
.tool-sub { font-size: 12px; color: #8A94AD; }

.package-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
}
.package-card {
  border: 1px solid #E5E9F2; border-radius: 10px; padding: 18px;
  text-align: center; cursor: pointer; background: #fff;
  transition: all .2s;
}
.package-card:hover { box-shadow: 0 6px 18px rgba(54,104,252,.08); }
.package-card.selected { border-color: #3668FC; background: #F5F8FF; }
.pkg-name { font-size: 14px; font-weight: 700; color: #18233C; }
.pkg-months { font-size: 22px; font-weight: 700; color: #3668FC; margin-top: 10px; }
.pkg-unit { font-size: 12px; font-weight: 400; color: #8A94AD; margin-left: 2px; }
.pkg-price { font-size: 15px; color: #5A6580; margin-top: 6px; }
.pkg-symbol { font-size: 12px; }
.pkg-btn { margin-top: 14px; }
.pkg-empty { color: #B4BDCF; padding: 40px; text-align: center; grid-column: 1/-1; }

.time-text { color: #8A94AD; font-size: 12px; }
.price-main { font-size: 14px; font-weight: 700; color: #3668FC; }

/* 支付弹窗 */
.pay-summary {
  background: #F7F9FC; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px;
}
.pay-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; font-size: 13px; color: #5A6580; }
.pay-sn { color: #18233C; font-family: monospace; }
.pay-amount { font-size: 18px; font-weight: 700; color: #E5484D; }

.pay-type-tabs { display: flex; gap: 10px; margin-bottom: 16px; }
.pay-type {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
  border: 1px solid #E5E9F2; border-radius: 8px; padding: 10px 0;
  cursor: pointer; font-size: 14px; color: #5A6580; transition: all .2s;
}
.pay-type.active { border-color: #3668FC; color: #3668FC; background: #F5F8FF; font-weight: 600; }
.pay-icon {
  width: 22px; height: 22px; border-radius: 5px; color: #fff;
  font-style: normal; font-size: 12px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
}
.p-wechat { background: #07C160; }
.p-alipay { background: #1677FF; }

.qr-wrap { display: flex; flex-direction: column; align-items: center; min-height: 200px; justify-content: center; }
.qr-img { width: 220px; height: 220px; border: 1px solid #E5E9F2; border-radius: 8px; }
.qr-tip { font-size: 13px; color: #5A6580; margin-top: 10px; }
.qr-empty { color: #8A94AD; font-size: 14px; }
.qr-empty.qr-error { color: #E5484D; text-align: center; }
.qr-error-sub { font-size: 12px; color: #B4BDCF; margin-top: 6px; }
.qr-retry { margin-top: 14px; }
.pay-hint { font-size: 12px; color: #B4BDCF; text-align: center; margin-top: 12px; }

::v-deep .el-button--primary { background: #3668FC; border-color: #3668FC; }
::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }
::v-deep .el-dialog { border-radius: 12px; }
::v-deep .el-table__row:hover > td { background: #F5F8FF !important; }
</style>